import React, { useState, useEffect } from 'react';
import FileSelector from '../../files/FileSelector';
import FormInput from '../../general-components/FormInput';
import styles from './ImplantSource.module.css';
import SETTING_KEYS from '../../../constants/setting-keys';
import { ISource, useImplantContext } from '../../../store/implant-context';
import Button, { ButtonType } from '../../general-components/Button';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ImplantSource = ({
    regionalInfoIndex,
    regionalInfo,
    onUpdateRegionalInfoField,
}: {
    regionalInfoIndex: number;
    regionalInfo: any;
    onUpdateRegionalInfoField: (
        index: number,
        fieldName: string,
        value: any
    ) => void;
}) => {
    const { info } = useImplantContext();

    function handleIfuSelected(fileIds: string[]): void {
        onUpdateRegionalInfoField(regionalInfoIndex, 'ifu', fileIds[0]);
    }
    function handleImageSelected(fileIds: string[]): void {
        onUpdateRegionalInfoField(regionalInfoIndex, 'image', fileIds[0]);
    }
    function handleOtherFilesSelected(fileIds: string[]): void {
        onUpdateRegionalInfoField(regionalInfoIndex, 'otherFiles', fileIds);
    }

    const updateSourceField = (
        index: number,
        fieldName: string,
        value: any
    ) => {
        const updatedSources = JSON.parse(JSON.stringify(regionalInfo.sources));
        updatedSources[index] = {
            ...updatedSources[index],
            [fieldName]: value,
        };
        if (
            updatedSources[index].documentDate &&
            typeof updatedSources[index].documentDate === 'string'
        ) {
            updatedSources[index].documentDate = new Date(
                updatedSources[index].documentDate
            );
        }
        onUpdateRegionalInfoField(regionalInfoIndex, 'sources', updatedSources);
    };

    const formatDocumentDate = (value: Date | string | undefined) => {
        if (!value) {
            return undefined;
        }
        const date = value instanceof Date ? value : new Date(value);
        return isNaN(date.getTime())
            ? undefined
            : date.toISOString().split('T')[0];
    };

    const addSource = () => {
        const updatedSources = [...regionalInfo.sources];
        updatedSources.push({
            title: '',
            content: '',
            documentDate: new Date(),
        });
        onUpdateRegionalInfoField(regionalInfoIndex, 'sources', updatedSources);
    };

    const deleteSource = (index: number) => {
        const updatedSources = [...regionalInfo.sources];
        updatedSources.splice(index, 1);
        onUpdateRegionalInfoField(regionalInfoIndex, 'sources', updatedSources);
    };

    return (
        <div className={`${styles.wrapper}`}>
            <FileSelector
                onFilesSelected={handleIfuSelected}
                defaultFiles={regionalInfo.ifu ? [regionalInfo.ifu] : []}
                ownerType="manufacturer"
                manufacturerName={info[SETTING_KEYS.manufacturer]}
                allowMultiple={false}
                allowedTypes={{
                    document: true,
                    image: false,
                    video: false,
                }}
                label="IFU"
                disabled={!info[SETTING_KEYS.manufacturer]}
                disabledMessage="Please select a manufacturer first"
            />
            <FileSelector
                onFilesSelected={handleImageSelected}
                defaultFiles={regionalInfo.image ? [regionalInfo.image] : []}
                ownerType="manufacturer"
                manufacturerName={info[SETTING_KEYS.manufacturer]}
                allowMultiple={false}
                allowedTypes={{
                    document: false,
                    image: true,
                    video: false,
                }}
                label="Image"
                disabled={!info[SETTING_KEYS.manufacturer]}
                disabledMessage="Please select a manufacturer first"
            />

            <FileSelector
                onFilesSelected={handleOtherFilesSelected}
                defaultFiles={regionalInfo.otherFiles}
                ownerType="manufacturer"
                manufacturerName={info[SETTING_KEYS.manufacturer]}
                allowMultiple={true}
                allowedTypes={{
                    document: true,
                    image: true,
                    video: true,
                }}
                label="Other files"
                disabled={!info[SETTING_KEYS.manufacturer]}
                disabledMessage="Please select a manufacturer first"
            />
            <h3>Document sources(fill atleast 1)</h3>
            {regionalInfo.sources.map((source: ISource, index: number) => (
                <div className="flex flex-row gap-2 items-center">
                    <div className="w-1/2">
                        <FormInput
                            label="Title"
                            name="title"
                            type="text"
                            placeholder="Enter title of the source"
                            defaultValue={source.title}
                            onChange={(e) =>
                                updateSourceField(
                                    index,
                                    'title',
                                    e.target.value
                                )
                            }
                        />
                    </div>
                    <FormInput
                        required
                        label="Content"
                        name={source?.content}
                        type="text"
                        placeholder="Enter source of information. E.g. Manufacturer webiste or contact email etc."
                        defaultValue={source.content}
                        onChange={(e) =>
                            updateSourceField(index, 'content', e.target.value)
                        }
                    />
                    <div className="w-1/2">
                        <FormInput
                            label="Document date"
                            name="documentDate"
                            type="date"
                            defaultValue={formatDocumentDate(
                                source?.documentDate
                            )}
                            onChange={(e) => {
                                const isValid = !isNaN(
                                    Date.parse(e.target.value)
                                );
                                if (isValid) {
                                    updateSourceField(
                                        index,
                                        'documentDate',
                                        new Date(e.target.value)
                                    );
                                }
                            }}
                        />
                    </div>
                    <FontAwesomeIcon
                        icon={faXmark}
                        color="red"
                        className={styles.removeIcon}
                        onClick={() => deleteSource(index)}
                        title="Remove source"
                    />
                </div>
            ))}
            <div>
                <Button type={ButtonType.primary} onClick={addSource}>
                    Add Source
                </Button>
            </div>
        </div>
    );
};

export default ImplantSource;
